import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { visitsAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Label } from "../../../shared/components/ui/label";
import { Textarea } from "../../../shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import { toast } from "sonner";
import { LogOut, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { formatDateTime, getTimeSince, PURPOSE_LABELS } from "../../../shared/lib/utils";

export default function StationExitPage() {
  const navigate = useNavigate();
  const { visitId } = useParams();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [workCompleted, setWorkCompleted] = useState(true);
  const [equipmentSafe, setEquipmentSafe] = useState(true);
  const [switchingRestored, setSwitchingRestored] = useState(null);
  const [exitNotes, setExitNotes] = useState("");

  useEffect(() => {
    loadVisit();
  }, [visitId]);

  const loadVisit = async () => {
    try {
      const res = await visitsAPI.getById(visitId);
      setVisit(res.data);
      
      // Check if switching-related purpose
      if (res.data.purpose === "switching" || res.data.switching_order_number) {
        setSwitchingRestored(true);
      }
    } catch (err) {
      toast.error("Failed to load visit");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!equipmentSafe) {
      const confirm = window.confirm(
        "You indicated equipment is NOT in safe condition. Are you sure you want to proceed with exit?"
      );
      if (!confirm) return;
    }

    setSubmitting(true);
    try {
      await visitsAPI.exit(visitId, {
        work_completed: workCompleted,
        equipment_safe: equipmentSafe,
        switching_restored: switchingRestored,
        exit_notes: exitNotes || null,
      });

      toast.success("Station exit logged successfully");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log exit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  if (!visit || visit.status !== "active") {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Visit not found or already exited</p>
        <Button onClick={() => navigate("/")} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" data-testid="station-exit-page">
      <Button
        variant="ghost"
        onClick={() => navigate(`/visit/${visitId}`)}
        className="mb-4 font-mono text-sm"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Visit
      </Button>

      <Card className="border-2 border-border rounded-none">
        <CardHeader className="border-b border-border bg-muted/20">
          <CardTitle className="flex items-center gap-3 font-heading">
            <div className="h-10 w-10 bg-slate-800 dark:bg-slate-200 flex items-center justify-center">
              <LogOut className="h-5 w-5 text-white dark:text-slate-800" />
            </div>
            Station Exit
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Visit Summary */}
          <div className="mb-6 p-4 bg-muted/50 border-2 border-border">
            <h3 className="font-heading font-bold mb-3">{visit.station_name}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Entry Time
                </span>
                <p className="font-mono">{formatDateTime(visit.entry_time)}</p>
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Duration
                </span>
                <p className="font-mono">{getTimeSince(visit.entry_time)}</p>
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Purpose
                </span>
                <p className="font-mono">
                  {PURPOSE_LABELS[visit.purpose] || visit.purpose}
                </p>
              </div>
              {visit.work_order_number && (
                <div>
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    Work Order
                  </span>
                  <p className="font-mono">{visit.work_order_number}</p>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Work Completed */}
            <div className="flex items-center space-x-3 p-4 border-2 border-border bg-background">
              <Checkbox
                id="workCompleted"
                checked={workCompleted}
                onCheckedChange={setWorkCompleted}
                className="h-6 w-6 rounded-none"
                data-testid="work-completed-checkbox"
              />
              <Label
                htmlFor="workCompleted"
                className="font-body text-sm cursor-pointer flex items-center gap-2"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Work completed as planned
              </Label>
            </div>

            {/* Equipment Safe */}
            <div
              className={`flex items-center space-x-3 p-4 border-2 ${
                equipmentSafe
                  ? "border-emerald-500 bg-emerald-500/5"
                  : "border-red-500 bg-red-500/5"
              }`}
            >
              <Checkbox
                id="equipmentSafe"
                checked={equipmentSafe}
                onCheckedChange={setEquipmentSafe}
                className="h-6 w-6 rounded-none"
                data-testid="equipment-safe-checkbox"
              />
              <Label
                htmlFor="equipmentSafe"
                className={`font-body text-sm cursor-pointer ${
                  !equipmentSafe ? "text-red-600 dark:text-red-400" : ""
                }`}
              >
                Equipment left in safe condition
              </Label>
            </div>

            {/* Switching Restored (if applicable) */}
            {(visit.purpose === "switching" || visit.switching_order_number) && (
              <div className="flex items-center space-x-3 p-4 border-2 border-amber-500 bg-amber-500/5">
                <Checkbox
                  id="switchingRestored"
                  checked={switchingRestored === true}
                  onCheckedChange={setSwitchingRestored}
                  className="h-6 w-6 rounded-none"
                  data-testid="switching-restored-checkbox"
                />
                <Label
                  htmlFor="switchingRestored"
                  className="font-body text-sm cursor-pointer text-amber-700 dark:text-amber-400"
                >
                  Switching completed and restored as required
                </Label>
              </div>
            )}

            {/* Exit Notes */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Exit Notes
              </Label>
              <Textarea
                value={exitNotes}
                onChange={(e) => setExitNotes(e.target.value)}
                placeholder="Any additional notes about work performed or conditions..."
                className="rounded-none border-2 font-mono min-h-[100px]"
                data-testid="exit-notes-textarea"
              />
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border-2 border-amber-500">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-amber-700 dark:text-amber-400">
                This exit will be logged permanently. Exit time will be recorded as{" "}
                <span className="font-mono font-bold">NOW</span>. This action
                cannot be undone.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-14 rounded-sm bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-300 dark:text-slate-900 font-mono uppercase tracking-wider font-bold text-lg"
              data-testid="submit-exit-btn"
            >
              {submitting ? (
                <span className="spinner" />
              ) : (
                <>
                  <LogOut className="h-5 w-5 mr-2" />
                  Confirm Station Exit
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
